import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Input,
  Select,
  SelectItem,
  Switch,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  CtyButton,
  CtyShowToast,
  CtySpinner,
  ErrorBoundary,
  PageHeader,
  checkType,
  useHasApplicationResource,
} from '@circuitry-ai/doc-ui';
import {
  resolveTenantIdFromToken,
  useGetTenantToolsQuery,
  useToggleMcpMutation,
  useToggleTenantToolMutation,
  type McpToolVisibility,
  type RootState,
  type TenantTool,
} from '@circuitry-ai/doc-data';

const ALL_VISIBILITY_KEY = '__all__';
const VISIBILITY_ORDER: McpToolVisibility[] = ['internal', 'external'];

const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);

function getToolVisibility(tool: TenantTool): McpToolVisibility {
  return tool.visibility === 'external' ? 'external' : 'internal';
}

function formatVisibilityLabel(visibility: McpToolVisibility, t: (key: string) => string): string {
  return visibility === 'external' ? t('External') : t('Internal');
}

function ToolPermissionsSettings() {
  const { t } = useTranslation();
  const hasIntegrationsAccess = useHasApplicationResource('integrations');
  const token = useSelector((state: RootState) => state.auth.token) ?? '';
  const tenantId = useMemo(() => resolveTenantIdFromToken(token), [token]);

  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState(ALL_VISIBILITY_KEY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skipQueries = !tenantId;
  const {
    data: tenantToolsData = [],
    isLoading: toolsLoading,
    isError: toolsError,
    error: toolsQueryError,
    refetch: refetchTenantTools,
  } = useGetTenantToolsQuery(tenantId, { skip: skipQueries, refetchOnMountOrArgChange: true });
  const [toggleTenantTool] = useToggleTenantToolMutation();
  const [toggleMcp, { isLoading: mcpToggling }] = useToggleMcpMutation();

  // Backend defaults MCP to enabled for a tenant; there is no status endpoint to read on load.
  const [mcpActive, setMcpActive] = useState(true);

  const tools = tenantToolsData;
  const loading = skipQueries || toolsLoading;

  useEffect(() => {
    if (!tenantId) {
      setError(t('Authentication required to load MCP tools.'));
      return;
    }
    if (toolsError) {
      const queryError = toolsQueryError;
      const message =
        queryError && typeof queryError === 'object' && 'message' in queryError
          ? String((queryError as { message?: string }).message)
          : t('Failed to load MCP tools.');
      // The backend rejects the tools list while MCP is disabled for the tenant;
      // treat that as state, not a load failure, since there's no separate status endpoint.
      if (/mcp is disabled/i.test(message)) {
        setMcpActive(false);
        setError(null);
        return;
      }
      setError(message);
      return;
    }
    setError(null);
  }, [tenantId, toolsError, toolsQueryError, t]);

  const [pendingEnabled, setPendingEnabled] = useState<Record<string, boolean>>({});

  const getEffectiveEnabled = useCallback(
    (tool: TenantTool) => pendingEnabled[tool.capability_id] ?? tool.tenant_enabled,
    [pendingEnabled]
  );

  const filteredTools = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tools.filter((tool) => {
      if (visibilityFilter !== ALL_VISIBILITY_KEY && getToolVisibility(tool) !== visibilityFilter) {
        return false;
      }
      if (!q) return true;
      return (
        tool.name.toLowerCase().includes(q) ||
        (tool.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [tools, searchQuery, visibilityFilter]);

  const groupedTools = useMemo(() => {
    const groups = new Map<McpToolVisibility, TenantTool[]>();
    for (const tool of filteredTools) {
      const visibility = getToolVisibility(tool);
      const list = groups.get(visibility) ?? [];
      list.push(tool);
      groups.set(visibility, list);
    }
    return VISIBILITY_ORDER.filter((visibility) => groups.has(visibility)).map((visibility) => ({
      source: visibility,
      label: formatVisibilityLabel(visibility, t),
      items: (groups.get(visibility) ?? []).toSorted((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [filteredTools, t]);

  const sourceSelectItems = useMemo(
    () => [
      { key: ALL_VISIBILITY_KEY, label: t('All types') },
      ...VISIBILITY_ORDER.map((visibility) => ({
        key: visibility,
        label: formatVisibilityLabel(visibility, t),
      })),
    ],
    [t]
  );

  const pendingChanges = useMemo(() => {
    const changes: { capabilityId: string; enabled: boolean }[] = [];
    for (const tool of tools) {
      const next = pendingEnabled[tool.capability_id];
      if (next !== undefined && next !== tool.tenant_enabled) {
        changes.push({ capabilityId: tool.capability_id, enabled: next });
      }
    }
    return changes;
  }, [tools, pendingEnabled]);

  const hasPendingChanges = pendingChanges.length > 0;

  const setToolPending = (capabilityId: string, enabled: boolean) => {
    setPendingEnabled((prev) => {
      const tool = tools.find((x) => x.capability_id === capabilityId);
      if (!tool) return prev;
      if (enabled === tool.tenant_enabled) {
        const next = { ...prev };
        delete next[capabilityId];
        return next;
      }
      return { ...prev, [capabilityId]: enabled };
    });
  };

  const handleToggleTool = (tool: TenantTool, enabled: boolean) => {
    if (!hasIntegrationsAccess) return;
    setToolPending(tool.capability_id, enabled);
  };

  const handleToggleGroup = (groupItems: TenantTool[], enabled: boolean) => {
    if (!hasIntegrationsAccess) return;
    setPendingEnabled((prev) => {
      const next = { ...prev };
      for (const tool of groupItems) {
        if (enabled === tool.tenant_enabled) {
          delete next[tool.capability_id];
        } else {
          next[tool.capability_id] = enabled;
        }
      }
      return next;
    });
  };

  const handleSaveChanges = async () => {
    if (!tenantId || pendingChanges.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      for (const change of pendingChanges) {
        await toggleTenantTool({
          tenantId,
          capabilityId: change.capabilityId,
          enabled: change.enabled,
        }).unwrap();
      }
      CtyShowToast(checkType.success, t('Tool access saved successfully'));
      setPendingEnabled({});
      await refetchTenantTools();
    } catch (e) {
      const message = e instanceof Error ? e.message : t('Failed to save tool access');
      setError(message);
      CtyShowToast(checkType.error, message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMcp = async (next: boolean) => {
    if (!hasIntegrationsAccess) return;
    const previous = mcpActive;
    setMcpActive(next);
    try {
      const response = await toggleMcp({ active: next }).unwrap();
      setMcpActive(response.active);
      CtyShowToast(
        checkType.success,
        response.active ? t('MCP enabled for this tenant') : t('MCP disabled for this tenant')
      );
      await refetchTenantTools();
    } catch (e) {
      setMcpActive(previous);
      const message = e instanceof Error ? e.message : t('Failed to update MCP status');
      CtyShowToast(checkType.error, message);
    }
  };

  const headerActions = (
    <CtyButton
      size="sm"
      color="primary"
      className="font-semibold px-4"
      isDisabled={!hasPendingChanges || saving || !hasIntegrationsAccess}
      isLoading={saving}
      onPress={() => void handleSaveChanges()}
    >
      {t('Save changes')}
    </CtyButton>
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-4 h-full min-h-[200px] items-center justify-center">
        <CtySpinner size="lg" />
        <span className="text-default-500">{t('Loading MCP tools…')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 h-full min-h-0">
      <PageHeaderWithErrorBoundary
        heading={t('MCP Tools')}
        description={t(
          'Enable MCP for this tenant and grant or revoke individual tool access. Disabled tools are unavailable to all advisors regardless of advisor-level configuration.'
        )}
        pageHeaderClass="!px-0 !gap-1 !pb-0"
        actionButton={headerActions}
      />

      {error && (
        <div className="rounded-lg border border-danger bg-danger/10 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-default-200 bg-content1 overflow-hidden">
        {/* Toolbar inside card */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-default-200">
          <Input
            classNames={{
              base: 'flex-1 min-w-[200px] max-w-none',
              inputWrapper: 'h-10 rounded-full border-default-300 bg-content1 shadow-none',
            }}
            size="sm"
            variant="bordered"
            placeholder={t('Search tools...')}
            value={searchQuery}
            onValueChange={setSearchQuery}
            startContent={
              <Icon icon="solar:magnifer-linear" className="text-default-400 shrink-0" width={18} />
            }
            isClearable
            onClear={() => setSearchQuery('')}
          />
          <Select
            classNames={{
              base: 'w-[160px] shrink-0',
              trigger: 'h-10 min-h-10 rounded-full border-default-300 bg-content1 shadow-none',
            }}
            size="sm"
            variant="bordered"
            aria-label={t('Filter by type')}
            items={sourceSelectItems}
            selectedKeys={[visibilityFilter]}
            onSelectionChange={(keys) => {
              const selected = Array.from(keys)[0];
              if (typeof selected === 'string') {
                setVisibilityFilter(selected);
              }
            }}
          >
            {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
          </Select>
          <div className="flex items-center gap-2 shrink-0 pl-1">
            <span className="text-sm font-medium text-default-600 whitespace-nowrap">
              {t('Enable MCP')}
            </span>
            <Switch
              size="sm"
              color="success"
              isSelected={mcpActive}
              isDisabled={!hasIntegrationsAccess || mcpToggling}
              aria-label={t('Enable MCP')}
              onValueChange={(next) => void handleToggleMcp(next)}
            />
          </div>
        </div>

        {/* Grouped tool list */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {!mcpActive ? (
            <div className="px-4 py-16 text-center text-warning">
              {t('MCP is disabled for this tenant. Enable it above to grant advisors access to tools.')}
            </div>
          ) : groupedTools.length === 0 ? (
            <div className="px-4 py-16 text-center text-default-400">{t('No tools match your filters.')}</div>
          ) : (
            groupedTools.map((group, groupIndex) => {
              const groupAllEnabled = group.items.every((tool) => getEffectiveEnabled(tool));

              return (
                <div
                  key={group.source}
                  className={groupIndex > 0 ? 'border-t border-default-200' : ''}
                >
                  {/* Group header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-default-50/80">
                    <span className="text-sm font-semibold text-foreground">
                      {group.label}{' '}
                      <span className="font-normal text-default-500">
                        · {t('{{count}} tools', { count: group.items.length })}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="text-sm text-primary font-medium hover:opacity-80 disabled:opacity-40"
                      disabled={!hasIntegrationsAccess || !mcpActive}
                      onClick={() => handleToggleGroup(group.items, !groupAllEnabled)}
                    >
                      {t('Toggle all')}
                    </button>
                  </div>

                  {/* Column headers */}
                  <div className="grid grid-cols-[minmax(0,1fr)_120px] items-center gap-x-4 px-4 py-2 border-b border-default-100">
                    <span className="text-xs font-medium text-default-400">{t('Tool')}</span>
                    <span className="text-xs font-medium text-default-400 text-right pr-2">
                      {t('Tenant access')}
                    </span>
                  </div>

                  {/* Tool rows */}
                  {group.items.map((tool) => {
                    const enabled = getEffectiveEnabled(tool);

                    return (
                      <div
                        key={tool.capability_id}
                        className="grid grid-cols-[minmax(0,1fr)_120px] gap-x-4 items-start px-4 py-4 border-b border-default-100 last:border-b-0 bg-content1"
                      >
                        <div className="min-w-0 pr-4">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="font-mono text-sm font-bold text-foreground leading-snug">
                              {tool.name}
                            </span>
                          </div>
                          {tool.description && (
                            <p
                              className="text-sm text-default-500 mt-1.5 leading-relaxed line-clamp-2"
                              title={tool.description}
                            >
                              {tool.description}
                            </p>
                          )}
                        </div>

                        <div className="flex justify-end items-center self-center pt-0.5">
                          <Switch
                            size="sm"
                            color="success"
                            isSelected={enabled}
                            isDisabled={!hasIntegrationsAccess || !mcpActive}
                            aria-label={t('Tenant access for {{name}}', { name: tool.name })}
                            onValueChange={(next) => handleToggleTool(tool, next)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default ToolPermissionsSettings;
